import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';

interface Merchant {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  totalClips: number;
  totalRevenue: number;
  status: 'active' | 'inactive';
}

const ManagerTools = () => {
  // Dummy merchant data
  const [merchants, setMerchants] = useState<Merchant[]>([
    {
      id: 1,
      name: 'CourtVision Sports',
      email: 'info@courtvision.com',
      phone: '+1-555-0123',
      address: '123 Sports Ave, City, State 12345',
      totalClips: 45,
      totalRevenue: 1200,
      status: 'active'
    },
    {
      id: 2,
      name: 'GameTime Analytics',
      email: 'contact@gametime.com',
      phone: '+1-555-0456',
      address: '456 Analytics St, City, State 12345',
      totalClips: 28,
      totalRevenue: 850,
      status: 'active'
    },
    {
      id: 3,
      name: 'SportsReplay Inc',
      email: 'support@sportsreplay.com',
      phone: '+1-555-0789',
      address: '789 Replay Blvd, City, State 12345',
      totalClips: 32,
      totalRevenue: 950,
      status: 'inactive'
    }
  ]);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Merchant>>({});

  const handleEdit = (merchant: Merchant) => {
    setEditingId(merchant.id);
    setEditForm(merchant);
  };

  const handleSave = (id: number) => {
    setMerchants(prev => prev.map(merchant => 
      merchant.id === id ? { ...merchant, ...editForm } : merchant
    ));
    setEditingId(null);
    setEditForm({});
    toast({
      title: "Merchant updated",
      description: "Merchant information has been successfully updated.",
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleInputChange = (field: keyof Merchant, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <h1 className="text-2xl font-bold mb-6">Manager Tools</h1>
      
      <div className="grid grid-cols-1 gap-6">
        {merchants.map((merchant) => (
          <Card key={merchant.id} className="p-6">
            <CardContent className="p-0">
              {editingId === merchant.id ? (
                // Edit Form
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={editForm.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={editForm.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        value={editForm.status || ''}
                        onChange={(e) => handleInputChange('status', e.target.value as 'active' | 'inactive')}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={editForm.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleSave(merchant.id)}>
                      Save
                    </Button>
                    <Button variant="outline" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // Display View
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">{merchant.name}</h3>
                      <p className="text-muted-foreground">{merchant.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      merchant.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {merchant.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p>{merchant.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p>{merchant.address}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Clips</p>
                      <p className="font-semibold">{merchant.totalClips}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="font-semibold">${merchant.totalRevenue}</p>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEdit(merchant)}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ManagerTools;
